import {
  Box,
  Button,
  Heading,
  HStack,
  Spinner,
  Stack,
  Text,
  useColorModeValue,
} from '@chakra-ui/react';
import { useCallback, useEffect, useState } from 'react';
import { FaPlus } from 'react-icons/fa6';
import { Link as RouterLink } from 'react-router-dom';
import { toast } from 'react-toastify';
import { createStoreGroup, listStoreGroups } from 'services/storeGroup';
import type {
  CreateStoreGroupPayload,
  StoreGroup,
} from 'services/interfaces/storeGroup';
import { usePermissions } from 'shared/hooks/usePermissions';
import CreateStoreGroupModal from './components/CreateStoreGroupModal';
import StoreGroupTable from './components/StoreGroupTable';

const StoreGroupPage = () => {
  const { e } = usePermissions();
  const canRead = e('store-group-read');
  const canCreate = e('store-group-create');
  const bgColor = useColorModeValue('white', 'gray.800');

  const [rows, setRows] = useState<StoreGroup[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [creating, setCreating] = useState(false);

  const loadGroups = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const groups = await listStoreGroups();
      setRows(groups);
    } catch {
      setError('Não foi possível carregar os grupos de lojas.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (canRead) loadGroups();
  }, [canRead, loadGroups]);

  const handleCreate = async (payload: CreateStoreGroupPayload) => {
    setCreating(true);
    try {
      await createStoreGroup(payload);
      toast.success('Grupo criado com sucesso.');
      setCreateModalOpen(false);
      await loadGroups();
    } catch {
      toast.error('Falha ao criar o grupo.');
    } finally {
      setCreating(false);
    }
  };

  if (!canRead) {
    return (
      <Stack
        h="100vh"
        align="center"
        justify="center"
        gap="1.5rem"
        textAlign="center"
        p="2rem"
      >
        <Heading size="lg" color="gray.600">
          Acesso Restrito
        </Heading>
        <Text color="gray.500" maxW="400px">
          Você não possui permissão para acessar os grupos de lojas. Solicite ao
          seu supervisor para liberar o acesso.
        </Text>
        <Box>
          <Button as={RouterLink} to="/" colorScheme="yooga">
            Voltar para a página inicial
          </Button>
        </Box>
      </Stack>
    );
  }

  return (
    <Stack
      minH="100vh"
      minW={{ lg: '740px' }}
      w={{ base: '90%', lg: '85%' }}
      m="auto"
      px={{ base: '1rem', lg: '4rem' }}
      pt={{ base: '1rem', lg: '1.5rem' }}
      pb={{ base: '2rem', lg: '4rem' }}
      gap="1.5rem"
    >
      <HStack justify="space-between" align="center" flexWrap="wrap" gap={3}>
        <Heading size="lg" color="gray.600">
          Grupos de lojas
        </Heading>

        {canCreate && (
          <Button
            size="sm"
            colorScheme="yooga"
            leftIcon={<FaPlus />}
            onClick={() => setCreateModalOpen(true)}
          >
            Novo grupo
          </Button>
        )}
      </HStack>

      <Box bg={bgColor} borderRadius="lg" shadow="sm" p="1.5rem">
        {error && (
          <Stack align="center" py="4rem" gap="1rem">
            <Text color="red.500">{error}</Text>
            <Button colorScheme="yooga" variant="outline" onClick={loadGroups}>
              Tentar novamente
            </Button>
          </Stack>
        )}

        {!error && loading && (
          <HStack justify="center" py="4rem">
            <Spinner size="lg" color="yooga.500" />
            <Text>Carregando…</Text>
          </HStack>
        )}

        {!error && !loading && rows.length === 0 && (
          <Text color="gray.500" py={8} textAlign="center">
            Nenhum grupo cadastrado ainda.
          </Text>
        )}

        {!error && !loading && rows.length > 0 && (
          <StoreGroupTable rows={rows} />
        )}
      </Box>

      <CreateStoreGroupModal
        isOpen={createModalOpen}
        onClose={() => setCreateModalOpen(false)}
        onConfirm={handleCreate}
        submitting={creating}
      />
    </Stack>
  );
};

export default StoreGroupPage;
